import { Table } from 'antd';
import { type ColumnFilterItem, type FilterDropdownProps } from 'antd/es/table/interface';
import { pick, uniqBy } from 'lodash/fp';
import { useCallback, useMemo, type Key } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type LabelsDataFragment } from '../../../api/fragments/LabelsData';
import { useListLabelsQuery } from '../../../api/queries/listLabels';
import { LabelsSortingField, CompanyFilterListCollection } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import { type PaginatedTableProps } from '../../../components/PaginatedTable';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import makeGetSortingRule from '../../../utilities/makeGetSortingRule';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useGetCompanyFilter from '../../../utilities/useGetCompanyFilter';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useTranslatedString from '../../../utilities/useTranslatedString';
import useListReducer from './useListReducer';

type LabelDataSource = LabelsDataFragment & { key: Key };

type PaginatedLabelsTable = PaginatedTableProps<LabelDataSource>;

export const getSortField = makeGetSortingRule((field): LabelsSortingField => {
    switch (field) {
        case 'labelModule.company.displayName':
            return LabelsSortingField.Company;

        case 'labelModule.displayName':
            return LabelsSortingField.ModuleName;

        case 'labelName':
            return LabelsSortingField.LabelName;

        case 'textColor':
            return LabelsSortingField.TextColor;

        default:
            throw new Error('Such Field is not supported');
    }
});

const LabelsList = () => {
    const { t } = useTranslation('labelsList');

    // get company context
    const company = useCompany(true);

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Label);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    const { page, pageSize, sort, filter } = state;

    const { data, loading } = useListLabelsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { companyIds: company ? [company.id] : undefined, ...filter },
        },
    });

    const companyFilterList = useGetCompanyFilter({ collection: CompanyFilterListCollection.Labels });

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort': {
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });
                }

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filter: {
                            companyIds: !filters.company ? undefined : (filters.company as string[]),
                            labelName: !filters.labelName ? null : filters.labelName[0],
                            textColor: !filters.textColor ? null : filters.textColor[0],
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const dataSource = useMemo(
        () => (data?.labels?.items || []).map(item => ({ ...item, key: item.id })),
        [data?.labels?.items]
    );

    const total = data?.labels?.count || 0;

    const navigate = useNavigate();

    const onRow = useCallback<PaginatedLabelsTable['onRow']>(
        record => ({
            onClick: () => {
                setCache(state);
                navigate(record.id);
            },
        }),
        [navigate, setCache, state]
    );

    const translated = useTranslatedString();

    const searchBox = useSearchInDropDown();

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('labelsList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['labelModule', 'company', 'displayName']}
                    filterDropdown={props => showFilterDropDown(props, companyFilterList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.companyIds}
                    sortOrder={sort?.field === LabelsSortingField.Company ? sort.orderValue : undefined}
                    title={t('labelsList:columns.company')}
                    sorter
                />
            )}
            <Table.Column
                key="labelName"
                dataIndex="labelName"
                filterDropdown={searchBox.render(t('labelsList:columns.labelName'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.labelName ? [filter.labelName] : undefined}
                render={value => translated(value)}
                sortOrder={sort?.field === LabelsSortingField.LabelName ? sort.orderValue : undefined}
                title={t('labelsList:columns.labelName')}
                sorter
            />
            <Table.Column
                key="textColor"
                dataIndex="textColor"
                filterDropdown={searchBox.render(t('labelsList:columns.textColor'))}
                filterIcon={renderSearchIcon}
                filteredValue={filter.textColor ? [filter.textColor] : undefined}
                sortOrder={sort?.field === LabelsSortingField.TextColor ? sort.orderValue : undefined}
                title={t('labelsList:columns.textColor')}
                sorter
            />
            <Table.Column
                key="moduleDisplayName"
                dataIndex={['labelModule', 'displayName']}
                sortOrder={sort?.field === LabelsSortingField.ModuleName ? sort.orderValue : undefined}
                title={t('labelsList:columns.module')}
                sorter
            />
            <Table.Column
                key="isActive"
                dataIndex="isActive"
                render={(value, record: LabelsDataFragment) => {
                    const array = [];
                    record.moduleVariants.forEach(moduleVariant =>
                        moduleVariant.addedVariants.forEach(variant => array.push(variant))
                    );

                    return uniqBy('versioning.suiteId', array).length;
                }}
                title={t('labelsList:columns.numberOfVariant')}
            />
        </PaginatedTableWithContext>
    );
};

export default LabelsList;
